import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  category?: string;
}

interface InvoiceDetail {
  _id: string;
  email_from: string;
  email_subject: string;
  email_date: string;
  status: string;
  source?: string;
  triage_result?: string;
  triage_confidence?: number;
  extracted_data?: {
    vendor_name?: string;
    vendor_nit?: string;
    invoice_number?: string;
    total_amount?: number;
    subtotal?: number;
    tax_amount?: number;
    rete_fuente?: number;
    rete_iva?: number;
    rete_ica?: number;
    currency?: string;
    line_items?: LineItem[];
  };
  classification?: {
    vendor_id?: string;
    expense_account_id?: string;
    memo?: string;
    verified?: boolean;
    tools_error?: string;
  };
  duplicate_of?: {
    netsuite_bill_id?: string;
    tranid?: string;
    trandate?: string;
    amount?: number;
  };
  netsuite_record?: {
    internal_id?: string;
    vendor_bill_id?: string;
    record_type?: string;
    status?: string;
    url?: string;
  };
  error_log?: Array<{
    message: string;
    timestamp: string;
    step?: string;
    stage?: string;
  }>;
  attachment_flags?: {
    password_protected?: boolean;
  };
}

const STATUS_COLORS: Record<string, string> = {
  pushed: 'badge-success',
  error: 'badge-error',
  classified: 'badge-info',
  triaging: 'badge-warning',
  extracting: 'badge-warning',
  needs_review: 'badge-warning',
  duplicate: 'badge-accent',
  quota_exceeded: 'badge-error',
  ignored: 'badge-ghost',
  received: 'badge-ghost',
};

function fmtAmount(amount: number | undefined, currency?: string): string {
  if (amount == null) return '-';
  return `${currency ?? '$'}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');

  const handleApprove = async () => {
    if (!id) return;
    setApproving(true);
    setApproveError('');
    try {
      const res = await apiFetch(`/tbit-invoices/api/records/${id}/approve`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al aprobar');
      // Reload record to show updated state
      const detailRes = await apiFetch(`/tbit-invoices/api/records/${id}`);
      if (detailRes.ok) setRecord(await detailRes.json());
    } catch (err: any) {
      setApproveError(err.message);
    } finally {
      setApproving(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    apiFetch(`/tbit-invoices/api/records/${id}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load invoice');
        return res.json();
      })
      .then(setRecord)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-base-300 rounded w-48 animate-pulse" />
        <div className="card bg-base-100 shadow animate-pulse">
          <div className="card-body space-y-3">
            <div className="h-4 bg-base-300 rounded w-64" />
            <div className="h-4 bg-base-300 rounded w-48" />
            <div className="h-4 bg-base-300 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/tbit-invoices/dashboard" className="btn btn-ghost btn-sm">&larr; Back</Link>
        <div className="alert alert-error"><span>{error}</span></div>
      </div>
    );
  }

  if (!record) return null;

  const statusClass = STATUS_COLORS[record.status] || 'badge-ghost';
  const statusLabel = record.status.replace(/_/g, ' ');
  const ext = record.extracted_data;
  const ns = record.netsuite_record;
  const hasRetentions = ext && ((ext.rete_fuente ?? 0) > 0 || (ext.rete_iva ?? 0) > 0 || (ext.rete_ica ?? 0) > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/tbit-invoices/dashboard" className="btn btn-ghost btn-sm">&larr; Back</Link>
        <h1 className="text-2xl font-bold">Invoice Detail</h1>
        <span className={`badge ${statusClass}`}>{statusLabel}</span>
        {record.source === 'manual' && (
          <span className="badge badge-outline badge-sm">Manual Upload</span>
        )}
      </div>

      {/* Needs review explanation */}
      {record.status === 'needs_review' && (
        <div className="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold">Este email requiere tu revisión</p>
            <p className="text-sm">La IA detectó que este email está relacionado con cuentas por pagar, pero no pudo confirmar que sea una factura procesable. Puede ser un estado de cuenta, notificación de envío, u otro documento.</p>
          </div>
          <Link to="/tbit-invoices/dashboard" className="btn btn-sm btn-ghost">Ignorar</Link>
        </div>
      )}

      {/* Ignored explanation */}
      {record.status === 'ignored' && (
        <div className="alert">
          <span className="text-sm">Este email fue descartado automáticamente — no parece ser una factura.</span>
        </div>
      )}

      {/* Password-protected ZIP warning */}
      {record.attachment_flags?.password_protected && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold">Adjunto protegido con contraseña</p>
            <p className="text-sm">El archivo ZIP adjunto tiene contraseña y no se pudo extraer la factura. Sube el PDF manualmente.</p>
          </div>
          <Link to="/tbit-invoices/upload" className="btn btn-sm btn-outline">Subir PDF</Link>
        </div>
      )}

      {/* Zero amount warning */}
      {record.status === 'classified' && record.extracted_data && (record.extracted_data.total_amount === 0 || record.extracted_data.subtotal === 0) && !record.extracted_data.line_items?.length && (
        <div className="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-semibold">No se pudieron extraer montos</p>
            <p className="text-sm">La factura muestra $0 — probablemente los datos estaban dentro de un adjunto que no se pudo leer. Verifica el contenido antes de aprobar.</p>
          </div>
        </div>
      )}

      {/* Unverified classification warning */}
      {record.classification && record.classification.verified === false && (
        <div className="alert alert-warning">
          <div className="flex-1">
            <p className="font-semibold">Clasificación no verificada</p>
            <p className="text-sm">Los IDs de proveedor y cuenta fueron estimados por IA sin validar contra NetSuite. Verifica los datos antes de aprobar.</p>
            {record.classification.tools_error && (
              <p className="text-xs mt-1 opacity-60">Error: {record.classification.tools_error}</p>
            )}
          </div>
        </div>
      )}

      {/* Approve / Retry action */}
      {(record.status === 'classified' || record.status === 'error') && (
        <div className={`alert ${record.status === 'error' ? 'alert-error' : 'alert-info'}`}>
          <div className="flex-1">
            <p className="font-semibold">
              {record.status === 'classified'
                ? 'Factura lista para enviar a NetSuite'
                : 'Error al procesar — puedes reintentar'}
            </p>
            {approveError && <p className="text-sm mt-1">{approveError}</p>}
          </div>
          <button
            className={`btn btn-sm ${record.status === 'error' ? 'btn-warning' : 'btn-primary'}`}
            onClick={handleApprove}
            disabled={approving}
          >
            {approving
              ? <><span className="loading loading-spinner loading-xs" /> Enviando...</>
              : record.status === 'error'
                ? 'Reintentar'
                : 'Aprobar y enviar a NetSuite'}
          </button>
        </div>
      )}

      {/* NetSuite success */}
      {record.status === 'pushed' && record.netsuite_record?.url && (
        <div className="alert alert-success">
          <span>Factura enviada a NetSuite</span>
          <a
            href={record.netsuite_record.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-success"
          >
            Ver en NetSuite
          </a>
        </div>
      )}

      {/* Triage info */}
      {record.triage_result && (
        <div className="card bg-base-100 shadow">
          <div className="card-body py-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="font-semibold text-base-content/50">Triage:</span>
              <span className={`badge badge-sm ${record.triage_result === 'invoice' ? 'badge-success' : record.triage_result === 'needs_review' ? 'badge-warning' : 'badge-ghost'}`}>
                {record.triage_result}
              </span>
              {record.triage_confidence != null && (
                <span className="text-base-content/40">
                  {(record.triage_confidence * 100).toFixed(0)}% confidence
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Duplicate info */}
      {record.status === 'duplicate' && record.duplicate_of && (
        <div className="alert alert-warning">
          <div>
            <p className="font-semibold">Duplicate detected</p>
            <p className="text-sm">
              This invoice matches an existing NetSuite bill:
              {record.duplicate_of.tranid && <> <strong>{record.duplicate_of.tranid}</strong></>}
              {record.duplicate_of.trandate && <> dated {record.duplicate_of.trandate}</>}
              {record.duplicate_of.amount != null && <> for {fmtAmount(Math.abs(record.duplicate_of.amount))}</>}
            </p>
          </div>
        </div>
      )}

      {/* Email metadata */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm uppercase tracking-wide text-base-content/50">Email</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            <div>
              <span className="font-semibold">From:</span> {record.email_from}
            </div>
            <div>
              <span className="font-semibold">Subject:</span> {record.email_subject}
            </div>
            <div>
              <span className="font-semibold">Date:</span>{' '}
              {new Date(record.email_date).toLocaleString('en-US')}
            </div>
          </div>
        </div>
      </div>

      {/* Extracted data */}
      {ext && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm uppercase tracking-wide text-base-content/50">
              Extracted Data
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {ext.vendor_name && (
                <div>
                  <span className="font-semibold block text-base-content/50">Vendor</span>
                  {ext.vendor_name}
                  {ext.vendor_nit && (
                    <span className="text-xs text-base-content/40 block">NIT: {ext.vendor_nit}</span>
                  )}
                </div>
              )}
              {ext.invoice_number && (
                <div>
                  <span className="font-semibold block text-base-content/50">Invoice #</span>
                  {ext.invoice_number}
                </div>
              )}
              {ext.subtotal != null && (
                <div>
                  <span className="font-semibold block text-base-content/50">Subtotal</span>
                  {fmtAmount(ext.subtotal, ext.currency)}
                </div>
              )}
              {ext.tax_amount != null && (
                <div>
                  <span className="font-semibold block text-base-content/50">IVA</span>
                  {fmtAmount(ext.tax_amount, ext.currency)}
                </div>
              )}
              {ext.total_amount != null && (
                <div>
                  <span className="font-semibold block text-base-content/50">Total</span>
                  <span className="text-lg font-bold text-primary">
                    {fmtAmount(ext.total_amount, ext.currency)}
                  </span>
                </div>
              )}
            </div>

            {/* Retention breakdown */}
            {hasRetentions && (
              <div className="mt-4 p-3 bg-base-200 rounded-lg">
                <h3 className="font-semibold text-sm mb-2">Retentions</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-base-content/50 block">ReteFuente</span>
                    <span className="font-mono">{fmtAmount(ext.rete_fuente, ext.currency)}</span>
                  </div>
                  <div>
                    <span className="text-base-content/50 block">ReteIVA</span>
                    <span className="font-mono">{fmtAmount(ext.rete_iva, ext.currency)}</span>
                  </div>
                  <div>
                    <span className="text-base-content/50 block">ReteICA</span>
                    <span className="font-mono">{fmtAmount(ext.rete_ica, ext.currency)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Line items */}
            {ext.line_items && ext.line_items.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold text-sm mb-2">Line Items</h3>
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Category</th>
                        <th className="text-right">Qty</th>
                        <th className="text-right">Unit Price</th>
                        <th className="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ext.line_items.map((item, i) => (
                        <tr key={i}>
                          <td>{item.description}</td>
                          <td className="text-xs text-base-content/50">{item.category ?? '-'}</td>
                          <td className="text-right">{item.quantity}</td>
                          <td className="text-right font-mono">
                            {fmtAmount(item.unit_price, ext.currency)}
                          </td>
                          <td className="text-right font-mono">
                            {fmtAmount(item.amount, ext.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NetSuite record */}
      {ns && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm uppercase tracking-wide text-base-content/50">
              NetSuite Record
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {(ns.internal_id || ns.vendor_bill_id) && (
                <div>
                  <span className="font-semibold block text-base-content/50">Internal ID</span>
                  {ns.internal_id || ns.vendor_bill_id}
                </div>
              )}
              {ns.record_type && (
                <div>
                  <span className="font-semibold block text-base-content/50">Type</span>
                  {ns.record_type}
                </div>
              )}
              {ns.status && (
                <div>
                  <span className="font-semibold block text-base-content/50">Status</span>
                  {ns.status}
                </div>
              )}
              {ns.url && (
                <div>
                  <span className="font-semibold block text-base-content/50">Link</span>
                  <a href={ns.url} target="_blank" rel="noopener noreferrer" className="link link-primary text-sm">
                    Open in NetSuite
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error log */}
      {record.error_log && record.error_log.length > 0 && (
        <div className="card bg-base-100 shadow border border-error/20">
          <div className="card-body">
            <h2 className="card-title text-sm uppercase tracking-wide text-error">Errors</h2>
            <div className="space-y-2">
              {record.error_log.map((err, i) => (
                <div key={i} className="bg-error/5 rounded p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    {(err.step || err.stage) && (
                      <span className="badge badge-error badge-outline badge-xs">{err.step || err.stage}</span>
                    )}
                    <span className="text-xs text-base-content/40">
                      {new Date(err.timestamp).toLocaleString('en-US')}
                    </span>
                  </div>
                  <p className="text-error">{err.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
